/**
 * The locale a figure is written in: the reader's chosen language (uiv2 7c).
 *
 * Until 7c, 70 calls to `toLocaleString()`/`toLocaleDateString()` passed no
 * locale and so followed the BROWSER — an English reader on a French browser
 * read "1 234,5" beside English words — and 44 more hardcoded `'en-US'` or
 * `'en-CA'`. Every formatter now asks here.
 *
 * Reads the i18next singleton (not `useTranslation`), like `lib/api-error.ts`:
 * the formatters are plain functions called from render, exports and charts.
 * An uninitialised i18next (a unit test that never loads `@/i18n`) reads as
 * English.
 */
import i18next from 'i18next';
import { SUPPORTED_LOCALES, type AppLocale } from '@/i18n/locales';

/**
 * The Intl tag per reader language. English is `en-US`: numbers are the same
 * as `en-CA`, and its times read "2:05 PM" as the app and the mockup always
 * have (`en-CA` writes "2:05 p.m."). French is Canadian (`1 234,50 $`, the
 * product's market). Arabic is pinned to Latin digits (`-u-nu-latn`) — every
 * figure the app draws is Latin, and ICU's default for a bare `ar` has
 * changed between releases.
 */
const INTL_TAG: Record<AppLocale, string> = {
  en: 'en-US',
  fr: 'fr-CA',
  ar: 'ar-u-nu-latn',
};

function asAppLocale(lng: string | undefined): AppLocale {
  const base = (lng ?? '').split('-')[0] as AppLocale;
  return SUPPORTED_LOCALES.includes(base) ? base : 'en';
}

/** The reader's language, or the one given (a machine writer passes `'en'`). */
export function readerLocale(lng?: string): AppLocale {
  return asAppLocale(lng ?? i18next.language);
}

/** The Intl locale tag for the reader's language, or for the one given. */
export function formatLocale(lng?: string): string {
  return INTL_TAG[readerLocale(lng)];
}

/**
 * Keeps a figure's sign beside its digits inside right-to-left text.
 *
 * In an Arabic sentence `−$842.17` drew as `$842.17−`: a leading minus or plus
 * is a neutral character, and the bidi algorithm gives it the paragraph's
 * direction (seen in the 7b·S1 screenshots; no probe had it). A left-to-right
 * isolate (U+2066 … U+2069) makes the figure one left-to-right run. Only in a
 * right-to-left language; the marks are invisible. A PDF strips them
 * (`stripIsolates`) — jsPDF's WinAnsi fonts cannot draw them.
 */
export function ltrFigure(text: string, lng?: string): string {
  return readerLocale(lng) === 'ar' ? `\u2066${text}\u2069` : text;
}

const ISOLATES = /[\u2066-\u2069\u200e\u200f]/g;

/** A figure with its direction marks removed, for a writer that cannot draw them. */
export function stripIsolates(text: string): string {
  return text.replace(ISOLATES, '');
}
